import React, { useState, useEffect, useRef, useMemo } from 'react';
import { M39AnchorMenu, Spinner } from 'ui/components';

export const M39AnchorMenuModuleFeature: React.FC<Umbraco.M39AnchorMenuModule> = ({ content }) => {
	const { anchors } = content?.properties ?? {};
	const [activeId, setActiveId] = useState<string | undefined>();
	const activeIdRef = useRef<string | undefined>(undefined);
	const [isReady, setIsReady] = useState(false);
	const navigationLabel = content?.properties?.navigationLabel ?? 'Table of contents';
	const navRef = useRef<HTMLElement>(null);
	const sectionRefsRef = useRef<Map<string, HTMLElement>>(new Map());
	const initialHashScrollDoneRef = useRef<boolean>(false);

	const flattenAnchors = useMemo(() => anchors?.map((anchorItem) => anchorItem?.content?.properties) ?? [], [anchors]);

	const setActive = (id: string | undefined) => {
		if (activeIdRef.current === id) return;
		activeIdRef.current = id;
		setActiveId(id);
	};

	const scrollToElement = (el: HTMLElement, behavior: ScrollBehavior = 'smooth') => {
		const navHeight = navRef.current?.offsetHeight ?? 0;
		const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
		window.scrollTo({ top, behavior });
	};

	// Initial hash scroll — deferred past hydration paint via double rAF
	useEffect(() => {
		const hash = window.location.hash.replace('#', '');
		if (!hash) {
			initialHashScrollDoneRef.current = true;
			setIsReady(true);
			return;
		}

		let resizeObserver: ResizeObserver | null = null;
		let stabilizeTimer: ReturnType<typeof setTimeout>;
		let fallbackTimer: ReturnType<typeof setTimeout>;

		const openGate = () => {
			resizeObserver?.disconnect();
			resizeObserver = null;
			clearTimeout(stabilizeTimer);
			clearTimeout(fallbackTimer);
			requestAnimationFrame(() => {
				initialHashScrollDoneRef.current = true;
				setIsReady(true);
			});
		};

		const scrollToHash = () => {
			const el = document.getElementById(hash);
			if (el) {
				setActive(hash);
				scrollToElement(el, 'instant' as ScrollBehavior);
			}
		};

		const scrollToTargetWithObserver = () => {
			scrollToHash();

			// Watch document body for layout shifts caused by lazy-loaded content.
			resizeObserver = new ResizeObserver(() => {
				scrollToHash();
				clearTimeout(stabilizeTimer);
				stabilizeTimer = setTimeout(openGate, 300);
			});

			resizeObserver.observe(document.body);
			stabilizeTimer = setTimeout(openGate, 300);
			// Hard cap: open gate after 3 s regardless (broken lazy-loaders, etc.).
			fallbackTimer = setTimeout(openGate, 3000);
		};

		// Double rAF ensures the browser has painted and laid out before we scroll.
		const scheduleScroll = () => requestAnimationFrame(() => requestAnimationFrame(scrollToTargetWithObserver));

		if (document.readyState === 'complete') {
			scheduleScroll();
		} else {
			window.addEventListener('load', scheduleScroll, { once: true });
		}

		return () => {
			window.removeEventListener('load', scheduleScroll);
			resizeObserver?.disconnect();
			clearTimeout(stabilizeTimer);
			clearTimeout(fallbackTimer);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// hashchange fires only from user-initiated navigation (anchor clicks, back/forward).
	useEffect(() => {
		const onHashChange = () => {
			const hash = window.location.hash.replace('#', '');
			setActive(hash || undefined);

			if (hash) {
				const el = sectionRefsRef.current.get(hash) ?? document.getElementById(hash);
				if (el) scrollToElement(el, 'smooth');
			}
		};
		window.addEventListener('hashchange', onHashChange);
		return () => window.removeEventListener('hashchange', onHashChange);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Tracks visible sections via IntersectionObserver to highlight the active anchor; clears active state when scrolled above all sections.
	useEffect(() => {
		if (!flattenAnchors.length) return;

		const map = new Map<string, HTMLElement>();
		flattenAnchors.forEach(({ anchor }) => {
			if (anchor) {
				const el = document.getElementById(anchor);
				if (el) map.set(anchor, el);
			}
		});
		sectionRefsRef.current = map;

		const anchorIds = flattenAnchors.map((a) => a.anchor).filter(Boolean) as string[];
		const visibleSet = new Set<string>();

		const pickActive = () => {
			if (!initialHashScrollDoneRef.current) return;

			const navH = navRef.current?.offsetHeight ?? 0;

			// IO signals that at least one section is visible below the nav.
			const first = anchorIds.find((id) => visibleSet.has(id));
			if (!first) return;

			const scrollTop = window.scrollY + navH;
			let bestMatch = first;
			for (const id of anchorIds) {
				const el = map.get(id);
				if (el && el.getBoundingClientRect().top + window.scrollY <= scrollTop) {
					bestMatch = id;
				}
			}

			// Force the last visible anchor as active
			const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
			if (nearBottom) {
				const lastVisible = [...anchorIds].reverse().find((id) => visibleSet.has(id));
				if (lastVisible) bestMatch = lastVisible;
			}

			setActive(bestMatch);
			window.history.replaceState(null, '', `#${bestMatch}`);
		};

		const navHeight = navRef.current?.offsetHeight ?? 0;
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						visibleSet.add(entry.target.id);
					} else {
						visibleSet.delete(entry.target.id);
					}
				});
				pickActive();
			},
			{
				root: null,
				rootMargin: `-${navHeight}px 0px 0px 0px`,
				threshold: 0,
			},
		);

		map.forEach((el) => observer.observe(el));

		// Clear active when above all sections.
		const onScroll = () => {
			if (!initialHashScrollDoneRef.current) return;

			const navH = navRef.current?.offsetHeight ?? 0;
			if (window.scrollY < navH) {
				setActive(undefined);
				const { pathname, search } = window.location;
				window.history.replaceState(null, '', `${pathname}${search}`);
				return;
			}
		};

		window.addEventListener('scroll', onScroll, { passive: true });

		return () => {
			observer.disconnect();
			window.removeEventListener('scroll', onScroll);
		};
	}, [flattenAnchors, isReady]); // eslint-disable-line react-hooks/exhaustive-deps

	return isReady ? <M39AnchorMenu anchors={flattenAnchors} activeId={activeId} navigationLabel={navigationLabel} navRef={navRef} /> : <Spinner />;
};
